import { useState, useEffect, useRef } from 'react';
import {
  parseLastOrigin,
  LastOriginEntry,
  ParsedLastOrigin,
} from '@ais-ui/utils';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { cn } from '@ais-ui/utils';
import { ChevronDown, ChevronRight, Database } from 'lucide-react';
import { Separator } from '@ais-ui/ui-core';

// Helper to create a unique key for comparison
const getEntryFingerprint = (entry: LastOriginEntry): string => {
  return `${entry.category}|${entry.name}|${entry.sourceType}|${entry.timestampRaw}|${entry.latitude}|${entry.longitude}`;
};

// ============================================================================
// Entry Item Component
// ============================================================================

interface LastOriginEntryItemProps {
  entry: LastOriginEntry;
  formatDateTime: (date: Date | null) => string;
  isNew?: boolean;
}

const LastOriginEntryItem = ({
  entry,
  formatDateTime,
  isNew,
}: LastOriginEntryItemProps) => {
  return (
    <div
      className={cn(
        'flex items-start gap-3 py-2 transition-colors duration-700',
        isNew && 'animate-highlight',
      )}
    >
      <span className="text-muted-foreground text-xs font-medium w-16 shrink-0 pt-0.5">
        {entry.category}
      </span>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium">{entry.name}</span>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
          <span className="source-badge px-1.5 py-0.5 bg-muted rounded text-[10px] font-medium">
            {entry.sourceType}
          </span>
          {entry.timestamp && (
            <span className="tabular-nums">
              {formatDateTime(entry.timestamp)}
            </span>
          )}
          {entry.latitude !== null && entry.longitude !== null && (
            <span className="tabular-nums text-muted-foreground/70">
              {entry.latitude.toFixed(4)}, {entry.longitude.toFixed(4)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

interface LastOriginDisplayProps {
  /** The raw lastOrigin string from the target info */
  lastOrigin: string | undefined | null;
  /** Optional: Show as a section with header (default: true) */
  showHeader?: boolean;
  /** Optional: Custom class name */
  className?: string;
}

/**
 * LastOriginDisplay - Displays parsed lastOrigin data in a collapsible format.
 *
 * Features:
 * - Collapsed by default showing entry count
 * - Expands to show flat list of entries
 * - Graceful handling of empty/null data
 *
 * Used across all target types: Ship, Radar, Methyd, Environmental, DAS, Drone
 */
export const LastOriginDisplay = ({
  lastOrigin,
  showHeader = true,
  className,
}: LastOriginDisplayProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [updatedKeys, setUpdatedKeys] = useState<Set<string>>(new Set());
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const prevParsedRef = useRef<ParsedLastOrigin | null>(null);

  // Parse the lastOrigin data
  const parsed = parseLastOrigin(lastOrigin);

  // Track updates for animation - only highlight changed/new entries
  useEffect(() => {
    if (!parsed.isValid || parsed.totalCount === 0) {
      prevParsedRef.current = parsed;
      return;
    }

    if (prevParsedRef.current !== null && prevParsedRef.current.isValid) {
      // Build a set of previous entry fingerprints
      const prevFingerprints = new Set(
        prevParsedRef.current.entries.map(getEntryFingerprint),
      );

      // Find entries that are new or changed
      const changedKeys = new Set<string>();
      parsed.entries.forEach((entry, index) => {
        const fingerprint = getEntryFingerprint(entry);
        if (!prevFingerprints.has(fingerprint)) {
          // This entry is new or changed
          changedKeys.add(`${entry.category}-${entry.name}-${index}`);
        }
      });

      if (changedKeys.size > 0) {
        setUpdatedKeys(changedKeys);
        const timer = setTimeout(() => setUpdatedKeys(new Set()), 1300);
        prevParsedRef.current = parsed;
        return () => clearTimeout(timer);
      }
    }

    prevParsedRef.current = parsed;
  }, [lastOrigin, parsed]);

  // Don't render anything if no valid data (admin-only field)
  if (!parsed.isValid || parsed.totalCount === 0) {
    return null;
  }

  const content = (
    <div className={cn('', className)}>
      {/* Collapsible header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 w-full text-left py-1.5 hover:bg-accent/50 rounded -mx-1 px-1 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
        <span className="text-sm text-muted-foreground">
          {parsed.totalCount} {parsed.totalCount === 1 ? 'entry' : 'entries'}
        </span>
      </button>

      {/* Expanded content - no inner scroll, flows with parent */}
      {isExpanded && (
        <div className="mt-1 ml-6 divide-y divide-border/50">
          {parsed.entries.map((entry, index) => {
            const key = `${entry.category}-${entry.name}-${index}`;
            return (
              <LastOriginEntryItem
                key={key}
                entry={entry}
                formatDateTime={formatDateTimeWithTimeZone}
                isNew={updatedKeys.has(key)}
              />
            );
          })}
        </div>
      )}
    </div>
  );

  if (!showHeader) {
    return content;
  }

  return (
    <div className="grid gap-1">
      <div>
        <h3 className="text-base font-medium flex items-center gap-2">
          <Database className="size-4" />
          Data Source
        </h3>
        <Separator className="mt-2" />
      </div>
      {content}
    </div>
  );
};
